import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Empty, Select, type SelectProps, Spin, Tooltip } from "antd";
import debounce from "lodash/debounce";
import { type ApiRotaGetSearchParams, apiRotaGetSearch } from "pages/rota/api/apiRotaGetSearch";
import type { ApiRota } from "pages/rota/types/ApiRota";
import { type ReactNode, useMemo, useState } from "react";

const QTDE_MIN_CHARS = 3;
const debounceTimeout = 600;

export type TSelectRota = Omit<SelectProps, "onChange"> & {
	onChange?: (value: number, rota: ApiRota | undefined) => void;
	externalParams?: Partial<ApiRotaGetSearchParams>;
};

export function SelectRota({ placeholder, onChange, onClear, externalParams, ...rest }: TSelectRota) {
	const [searchValue, setSearchValue] = useState("");

	const params: ApiRotaGetSearchParams = useMemo(
		() => ({
			...externalParams,
			descricao: searchValue,
		}),
		[searchValue, externalParams],
	);

	const { data, isFetching, refetch } = useQuery({
		queryKey: ["apiRotaGetSearch", params],
		queryFn: () => apiRotaGetSearch({ params }),
		placeholderData: keepPreviousData,
	});

	const clear = (): void => {
		setSearchValue("");
	};

	const handleSearch = useMemo(() => {
		const loadOptions = (value: string) => {
			setSearchValue(value);

			if (value.length < QTDE_MIN_CHARS) {
				return;
			}

			refetch();
		};

		return debounce(loadOptions, debounceTimeout);
	}, [refetch]);

	const handleChange = (value: number): void => {
		const rotas = (data || []).find((c) => c.rot_idpk === value);

		if (onChange) {
			onChange(value, rotas || undefined);
		}
	};

	const handleClear = (): void => {
		if (onClear) {
			onClear();
		}

		clear();
	};

	const renderNotFoundData = (): ReactNode => <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;

	const selectOptions = (data || []).map((item) => (
		<Select.Option key={item.rot_idpk} value={item.rot_idpk} label={`Rota: ${item?.rot_identificacao || ""}`} className="zebra">
			<Tooltip placement="topLeft" title={`Rota: ${item?.rot_identificacao || ""}`}>
				<div>
					<div style={{ fontWeight: "bold" }}>{`Rota: ${item?.rot_identificacao || ""}`}</div>

					{item?.rot_transportador_nome && (
						<div style={{ fontSize: "13px", opacity: "0.7" }}>Transportador: {item?.rot_transportador_nome}</div>
					)}
				</div>
			</Tooltip>
		</Select.Option>
	));

	return (
		<Select
			allowClear={true}
			showSearch={true}
			placeholder={placeholder || "Selecione uma Rota"}
			filterOption={false}
			onSearch={handleSearch}
			onChange={handleChange}
			onClear={handleClear}
			optionLabelProp="label"
			notFoundContent={isFetching ? <Spin size="small" /> : renderNotFoundData()}
			style={{ width: "100%" }}
			{...rest}
		>
			{selectOptions}
		</Select>
	);
}
